import { api } from './api';
import { vectorSearchService } from './vectorSearchService';

export interface SearchRequest {
  query: string;
  maxResults?: number;
}

export interface SearchDocument {
  id: string;
  customerId: string;
  title: string;
  description: string;
  path: string;
  nodeType: string;
  status: string;
  indexedForSearch: boolean;
  createdAt: string;
  updatedAt: string;
  score: number;
  metadata: Record<string, any>;
}

export interface SearchMetadata {
  query: string;
  totalResults: number;
  executedAt: string;
  searchType: string;
  filters: any;
}

export interface SearchFacet {
  field: string;
  values: FacetValue[];
}

export interface FacetValue {
  value: string;
  count: number;
}

export interface SearchSuggestion {
  text: string;
  score: number;
}

export interface SearchResult {
  query: string;
  results: SearchDocument[];
  totalResults: number;
  executedAt: string;
  searchType: string;
  filters: any;
}

export interface VectorSearchRequest {
  query: string;
  maxResults?: number;
}

class SearchService {
  private baseUrl = '/index';

    async semanticSearch(request: SearchRequest): Promise<SearchResult> {
    try {
      // Busca semântica atualizada retorna o mesmo formato da API /bypath
      const response = await api.post(`${this.baseUrl}/search`, request);
      return response.data;
    } catch (error) {
      console.error('Erro na busca semântica:', error);
      throw error;
    }
  }

  async vectorSearch(request: VectorSearchRequest): Promise<SearchResult> {
    try {
      // Usar o vectorSearchService para buscar dados
      const vectorResult = await vectorSearchService.vectorSearch({
        query: request.query,
        page: request.maxResults || 5,
        pageSize: 20, // Tamanho padrão para busca vetorial
        similarityThreshold: 0.7
      });

      // Converter VectorSearchDocument[] para SearchDocument[]
      const documents: SearchDocument[] = vectorResult.documents.map(doc => ({
        id: doc.id,
        customerId: 'default',
        title: doc.title,
        description: doc.description || doc.content.substring(0, 200) + '...',
        path: `/documents/${doc.id}`,
        nodeType: 'document',
        status: 'published',
        indexedForSearch: true,
        createdAt: doc.createdAt || new Date().toISOString(),
        updatedAt: doc.updatedAt || new Date().toISOString(),
        score: doc.similarityScore,
        metadata: doc.metadata || {}
      }));

      return {
        query: request.query,
        results: documents,
        totalResults: vectorResult.totalResults,
        executedAt: new Date().toISOString(),
        searchType: 'Vector',
        filters: null
      };
    } catch (error) {
      console.error('Erro na busca vetorial:', error);
      throw error;
    }
  }


  async searchWithFacets(request: SearchRequest): Promise<SearchResult> {
    try {
      const response = await api.post(`${this.baseUrl}/with-facets`, request);
      return response.data;
    } catch (error) {
      console.error('Erro na busca com facets:', error);
      throw error;
    }
  }

  async getSuggestions(query: string, maxSuggestions: number = 5): Promise<string[]> {
    try {
      // Por enquanto, retornar sugestões baseadas em palavras-chave
      const keywords = [
        'procedimentos', 'operacionais', 'manual', 'relatório', 'anual', 'política',
        'segurança', 'informação', 'contrato', 'serviços', 'apresentação', 'institucional',
        'recursos', 'humanos', 'auditoria', 'interna', 'treinamento', 'desenvolvimento',
        'qualidade', 'conformidade', 'regulatória', 'financeiro', 'operacional'
      ];

      return keywords
        .filter(k => k.toLowerCase().includes(query.toLowerCase()))
        .slice(0, maxSuggestions);
    } catch (error) {
      console.error('Erro ao obter sugestões:', error);
      return [];
    }
  }

  async getRelatedTerms(term: string, maxTerms: number = 10): Promise<string[]> {
    try {
      // Implementar lógica de termos relacionados
      const relatedTerms: Record<string, string[]> = {
        'procedimentos': ['operacionais', 'padrão', 'normas', 'protocolos'],
        'relatório': ['anual', 'mensal', 'trimestral', 'auditoria'],
        'política': ['segurança', 'qualidade', 'recursos humanos', 'financeira'],
        'manual': ['usuário', 'técnico', 'procedimentos', 'operacional'],
        'contrato': ['serviços', 'fornecedor', 'cliente', 'comercial']
      };

      return relatedTerms[term.toLowerCase()] || [];
    } catch (error) {
      console.error('Erro ao obter termos relacionados:', error);
      return [];
    }
  }
}

export const searchService = new SearchService(); 